/**
 * Learning Plan Service
 * Handles communication with the learning plan conversation API endpoints
 */

import type {
  LearningPlanStartResponse,
  LearningPlanChatRequest,
  LearningPlanSSEEvent,
  LearningPlanConversationResponse,
  AllPlansResponse,
  UpskillPlanResponse,
  UpskillPlanMarkItemsResponse,
  GoalsResponse,
  GoalOption,
  CreatePlanRequest,
  RevisePlanRequest,
  ProbingQuestion,
} from '../types/api.types';
import api from './api';
import { reportError } from '../lib/errorManager';

const API_BASE_URL = import.meta.env.VITE_API_URL
  ? `${import.meta.env.VITE_API_URL}/api`
  : 'https://api.aifluens.com/api';

export interface LearningPlanStreamCallbacks {
  onToken: (token: string) => void;
  onStatus: (status: string) => void;
  onDone: (content: string, conversationId?: string, phase?: string, planGenerated?: boolean) => void;
  onError: (error: string) => void;
}

/** POST /api/v1/learning-plan/start — Start a new learning plan conversation */
export async function startConversation(): Promise<LearningPlanStartResponse> {
  const url = `${API_BASE_URL}/v1/learning-plan/start`;

  const response = await fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => null);
    throw new Error(errorData?.message || `Start conversation failed: ${response.status}`);
  }

  const json = await response.json();
  return json.data;
}

/**
 * POST /api/v1/learning-plan/chat — Stream chat messages (SSE)
 * Returns an AbortController to cancel the stream
 */
export function streamLearningPlanChat(
  request: LearningPlanChatRequest,
  callbacks: LearningPlanStreamCallbacks,
): AbortController {
  const controller = new AbortController();
  const url = `${API_BASE_URL}/v1/learning-plan/chat`;

  (async () => {
    try {
      const response = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(request),
        signal: controller.signal,
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        callbacks.onError(errorData.message || `Chat failed: ${response.status}`);
        return;
      }

      const reader = response.body?.getReader();
      if (!reader) {
        callbacks.onError('No response body');
        return;
      }

      const decoder = new TextDecoder();
      let buffer = '';

      const processSSEBlock = (block: string) => {
        const lines = block.split('\n');
        for (const line of lines) {
          if (line.startsWith('data: ')) {
            const jsonStr = line.slice(6);
            try {
              const data: LearningPlanSSEEvent = JSON.parse(jsonStr);

              switch (data.type) {
                case 'token':
                  callbacks.onToken(data.content);
                  break;
                case 'status':
                  callbacks.onStatus(data.content);
                  break;
                case 'done':
                  callbacks.onDone(data.content, data.conversationId, data.phase, data.planGenerated);
                  break;
                case 'error':
                  callbacks.onError(data.content);
                  break;
              }
            } catch (parseError) {
              reportError(parseError, {
                source: 'learningPlanService',
                operation: 'sse_parse',
                eventSnippet: jsonStr.slice(0, 500),
              });
            }
          }
        }
      };

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });

        const events = buffer.split('\n\n');
        buffer = events.pop() || '';

        for (const event of events) {
          if (!event.trim()) continue;
          processSSEBlock(event);
        }
      }

      if (buffer.trim()) {
        processSSEBlock(buffer);
      }
    } catch (error) {
      if ((error as Error).name === 'AbortError') return;
      callbacks.onError((error as Error).message || 'Stream error');
    }
  })();

  return controller;
}

/** GET /api/v1/learning-plan/:conversationId — Get conversation state */
export async function getConversation(conversationId: string): Promise<LearningPlanConversationResponse> {
  const response = await api.get(`/v1/learning-plan/${conversationId}`);
  return response.data.data;
}

/** POST /api/v1/learning-plan/:conversationId/generate-plan — Generate the upskill plan */
export async function generatePlan(conversationId: string): Promise<{ data: UpskillPlanResponse }> {
  const response = await api.post(`/v1/learning-plan/${conversationId}/generate-plan`);
  return response.data;
}

/** GET /api/v1/learning-plan/plans/all — Get all plans (V1 + V2) */
export async function getAllPlans(): Promise<AllPlansResponse> {
  const response = await api.get('/v1/learning-plan/plans/all');
  return response.data.data;
}

// ─── V2 Plan Creation Endpoints ───

/**
 * Static goals when API is unreachable or returns empty / malformed rows.
 * Keep in sync with NodeExpressPlanMyCareer TRACK_GOALS.
 */
const FALLBACK_TRACK_GOALS: GoalsResponse = {
  SE: [
    { key: 'productivity', label: 'Boost productivity', description: 'Automate code generation, testing, debugging and CI/CD setup with AI assistants. Write better prompts, pick the right tools, and cut time spent on repetitive work.' },
    { key: 'build_ai', label: 'Build AI features', description: 'Design and ship AI agents, LLM-powered features, and intelligent products. Learn prompt engineering, AI-safe architecture, and how to evaluate AI outputs.' },
    { key: 'stay_relevant', label: 'Stay relevant', description: 'Understand AI foundations, the evolving tooling landscape, and how AI-assisted development is reshaping how engineers write, test, and ship code.' },
    { key: 'career_switch', label: 'Career switch to AI', description: 'Build a strong AI foundation — from prompt engineering and AI tooling to agent development — and position yourself for an AI engineering role.' },
  ],
  EM: [
    { key: 'team_productivity', label: 'Team productivity', description: 'Equip your team with AI-powered automation, smarter debugging workflows, and effective prompting practices to ship faster with fewer bottlenecks.' },
    { key: 'lead_ai_team', label: 'Lead an AI team', description: 'Build technical depth in AI agents, risk assessment, and strategic decision-making so you can set direction, evaluate trade-offs, and unblock your engineers.' },
    { key: 'stay_relevant', label: 'Stay relevant', description: 'Develop the AI literacy to evaluate emerging tools, assess risks, and make informed build-vs-buy decisions — stay credible in conversations with your team.' },
    { key: 'career_switch', label: 'Career switch to AI leadership', description: 'Learn AI foundations, agent architectures, and risk management to confidently transition into a role leading AI-focused engineering teams.' },
    { key: 'transform_team', label: 'Transform team to AI-native', description: 'Embed AI into your team\'s daily workflows — adopt new tooling, automate processes, and establish communication practices that make AI adoption stick.' },
  ],
};

function normalizeGoalRow(raw: unknown): GoalOption | null {
  if (!raw || typeof raw !== 'object') return null;
  const r = raw as Record<string, unknown>;
  const keyRaw = r.key ?? r.goal_key ?? r.goalKey ?? r.id;
  const labelRaw = r.label ?? r.title ?? r.name;
  const key = keyRaw != null && String(keyRaw).trim() !== '' ? String(keyRaw) : null;
  const label = labelRaw != null && String(labelRaw).trim() !== '' ? String(labelRaw) : null;
  if (!key || !label) return null;
  const desc = r.description ?? r.desc;
  const description = desc != null ? String(desc) : '';
  return { key, label, description };
}

function normalizeTrack(arr: unknown): GoalOption[] {
  if (!Array.isArray(arr)) return [];
  return arr.map(normalizeGoalRow).filter((g): g is GoalOption => g != null);
}

/** Parse various API shapes; supports nested `data`, lowercase track keys, alternate field names */
export function parseGoalsPayload(payload: unknown): GoalsResponse {
  if (payload == null || typeof payload !== 'object' || Array.isArray(payload)) {
    return { SE: [], EM: [] };
  }
  let root = payload as Record<string, unknown>;
  const nested = root.data;
  if (nested != null && typeof nested === 'object' && !Array.isArray(nested)) {
    const inner = nested as Record<string, unknown>;
    if ('SE' in inner || 'EM' in inner || 'se' in inner || 'em' in inner) {
      root = inner;
    }
  }
  const goalsWrap = root.goals;
  if (
    goalsWrap != null &&
    typeof goalsWrap === 'object' &&
    !Array.isArray(goalsWrap) &&
    ('SE' in goalsWrap || 'EM' in goalsWrap || 'se' in goalsWrap || 'em' in goalsWrap)
  ) {
    root = goalsWrap as Record<string, unknown>;
  }
  const se = root.SE ?? root.se;
  const em = root.EM ?? root.em;
  return {
    SE: normalizeTrack(se),
    EM: normalizeTrack(em),
  };
}

/** GET /api/v1/learning-plan/goals — Fetch goal options for the dropdown */
export async function getGoals(): Promise<GoalsResponse> {
  try {
    const response = await api.get('/v1/learning-plan/goals');
    const body = response.data;
    const raw = body?.data !== undefined ? body.data : body;
    const parsed = parseGoalsPayload(raw);
    if (parsed.SE.length === 0 && parsed.EM.length === 0) {
      if (import.meta.env.DEV) {
        console.warn('[getGoals] API returned no usable goals; using bundled fallback.', { raw });
      }
      return FALLBACK_TRACK_GOALS;
    }
    return parsed;
  } catch (err) {
    if (import.meta.env.DEV) {
      console.warn('[getGoals] Request failed; using bundled fallback.', err);
    }
    return FALLBACK_TRACK_GOALS;
  }
}

export interface CreatePlanResult {
  plan: UpskillPlanResponse;
  /** True when the server returned an existing plan for the same scenario (HTTP 200) instead of creating a new one (HTTP 201). */
  reused: boolean;
}

/** POST /api/v1/learning-plan/create — Create a V2 plan */
export async function createPlan(request: CreatePlanRequest): Promise<CreatePlanResult> {
  const response = await api.post('/v1/learning-plan/create', request);
  return { plan: response.data.data, reused: response.status === 200 };
}

/** GET /api/v1/learning-plan/plans/:planId — Get a specific plan by ID */
export async function getPlanById(planId: number): Promise<UpskillPlanResponse> {
  const response = await api.get(`/v1/learning-plan/plans/${planId}`);
  return response.data.data;
}

/** GET /api/v1/learning-plan/active — Get the active V2 plan (returns null on 404) */
export async function getActivePlan(): Promise<UpskillPlanResponse | null> {
  try {
    const response = await api.get('/v1/learning-plan/active');
    return response.data.data;
  } catch (err: any) {
    if (err?.response?.status === 404) return null;
    throw err;
  }
}

/** GET /api/v1/learning-plan/probing-questions — Fetch probing MCQs for revision */
export async function getProbingQuestions(planId: number): Promise<ProbingQuestion[]> {
  const response = await api.get('/v1/learning-plan/probing-questions', { params: { plan_id: planId } });
  return response.data.data;
}

/** POST /api/v1/learning-plan/revise — Revise a plan with probing answers */
export async function revisePlan(request: RevisePlanRequest): Promise<UpskillPlanResponse> {
  const response = await api.post('/v1/learning-plan/revise', request);
  return response.data.data;
}

/** PATCH /api/v1/learning-plan/plans/:planId/items — Bulk mark V2 plan items done */
export async function markItemsDone(planId: number, itemIds: number[]): Promise<UpskillPlanMarkItemsResponse> {
  const response = await api.patch(`/v1/learning-plan/plans/${planId}/items`, { item_ids: itemIds });
  return response.data;
}
